import {
  CoverageWithSettlementOption,
  PolicyLevelCoverages,
  PolicyUnit,
  UnitLevelCoverages,
} from "@covertree/new-business-graphql";
import { createAsyncThunkSlice, typedReducerCreator } from "../../utils";

export type UnitWithCoverages = Pick<
  PolicyUnit,
  "exposureLocator" | "unitLevelCoverages"
>;

interface PolicyCustomizationOptionsState {
  policyLevelCoverages?: PolicyLevelCoverages;
  unitsWithCoverages?: UnitWithCoverages[];
}

const initialState: PolicyCustomizationOptionsState = {
  policyLevelCoverages: undefined,
  unitsWithCoverages: undefined,
};

export const policyCustomizationOptionsSlice = createAsyncThunkSlice({
  name: "policyCustomizationOptions",
  initialState,
  selectors: {
    selectCustomizationOptions: (state) => state,
  },
  reducers: (c) => {
    const { create } = typedReducerCreator(c);

    return {
      /**
       * Saves copy of policy level coverages and units with coverages in state
       */
      setCustomizationOptions: create.reducer<{
        policyLevelCoverages: PolicyLevelCoverages;
        unitsWithCoverages: UnitWithCoverages[];
      }>((state, { payload }) => {
        state.policyLevelCoverages = payload.policyLevelCoverages;
        state.unitsWithCoverages = payload.unitsWithCoverages;
      }),

      /**
       * Saves copy of coverage B/C in state
       */
      setCustomizationUnitsWithCoveragesCoverageBC: create.reducer<{
        coverageB: CoverageWithSettlementOption;
        coverageC: CoverageWithSettlementOption;
      }>((state, { payload }) => {
        state.unitsWithCoverages = state.unitsWithCoverages?.map((unit) => ({
          ...unit,
          unitLevelCoverages: {
            ...unit.unitLevelCoverages,
            coverageB: payload.coverageB,
            coverageC: payload.coverageC,
          },
        }));
      }),
      /**
       * Resets state to initial value
       */
      resetCustomizationOptionsState: create.reducer(() => initialState),
      /**
       * Removes revision dependent coverages from state
       */
      removeRevisionDependentCoveragesFromCustomizationOptions: create.reducer<{
        freshUnitLevelCoverages: UnitLevelCoverages;
      }>((state, { payload: { freshUnitLevelCoverages } }) => {
        state.unitsWithCoverages = state.unitsWithCoverages?.map((unit) => ({
          ...unit,
          unitLevelCoverages: Object.fromEntries(
            Object.entries(unit.unitLevelCoverages).map(([key, value]) => {
              if (typeof value === "string") {
                return [key, value];
              }

              const mappedKey = key as keyof Omit<
                UnitLevelCoverages,
                "__typename"
              >;

              return [
                mappedKey,
                {
                  ...value,
                  ...("valueIsHidden" in value &&
                  "valueIsHidden" in freshUnitLevelCoverages[mappedKey]
                    ? {
                        valueIsHidden:
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-expect-error
                          freshUnitLevelCoverages[mappedKey].valueIsHidden,
                        // Reset coverage value when transitioning from visible to hidden
                        ...("value" in freshUnitLevelCoverages[mappedKey] &&
                        !value.valueIsHidden &&
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        freshUnitLevelCoverages[mappedKey].valueIsHidden
                          ? {
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-expect-error
                              value: freshUnitLevelCoverages[mappedKey].value,
                            }
                          : {}),
                      }
                    : {}),
                  ...("settlementOptionIsHidden" in value &&
                  "settlementOptionIsHidden" in
                    freshUnitLevelCoverages[mappedKey]
                    ? // prettier-ignore
                      {
                      settlementOptionIsHidden:
                      freshUnitLevelCoverages[mappedKey]
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        .settlementOptionIsHidden,
                    }
                    : {}),
                  ...("allOtherPerilsDeductibleIsHidden" in value &&
                  "allOtherPerilsDeductibleIsHidden" in
                    freshUnitLevelCoverages[mappedKey]
                    ? // prettier-ignore
                      {
                      allOtherPerilsDeductibleIsHidden:
                      freshUnitLevelCoverages[mappedKey]
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        .allOtherPerilsDeductibleIsHidden,
                    }
                    : {}),
                  ...("waterDamageDeductibleIsHidden" in value &&
                  "waterDamageDeductibleIsHidden" in
                    freshUnitLevelCoverages[mappedKey]
                    ? // prettier-ignore
                      {
                      waterDamageDeductibleIsHidden:
                      freshUnitLevelCoverages[mappedKey]
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        .waterDamageDeductibleIsHidden,
                    }
                    : {}),
                  ...("windHailDeductibleIsHidden" in value &&
                  "windHailDeductibleIsHidden" in
                    freshUnitLevelCoverages[mappedKey]
                    ? // prettier-ignore
                      {
                      windHailDeductibleIsHidden:
                      freshUnitLevelCoverages[mappedKey]
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        .windHailDeductibleIsHidden,
                    }
                    : {}),
                },
              ];
            })
          ) as UnitLevelCoverages,
        }));
      }),
    };
  },
});

export const {
  selectors: { selectCustomizationOptions },
  actions: {
    setCustomizationOptions,
    setCustomizationUnitsWithCoveragesCoverageBC,
    resetCustomizationOptionsState,
    removeRevisionDependentCoveragesFromCustomizationOptions,
  },
} = policyCustomizationOptionsSlice;
